import React from 'react';
import { Box, Card, CardContent, Typography, Button, Alert } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props { children: React.ReactNode; }
interface State { error: Error | null; }

// App-wide safety net. A render error anywhere below this point would otherwise
// unmount the whole React tree and leave a blank white page. Here we catch it,
// log it (so it shows up in the browser console / any console capture), and show
// a recoverable message instead of a dead screen.
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[Skonto] UI-Fehler:', error, info.componentStack);
  }

  handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Box sx={{
        minHeight: '100vh', bgcolor: 'background.default',
        display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
      }}>
        <Card sx={{ maxWidth: 480, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Etwas ist schiefgelaufen
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Die Seite konnte nicht angezeigt werden. Bitte lade die Seite neu.
              Falls das Problem bleibt, schick uns die untenstehende Meldung.
            </Typography>
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left', wordBreak: 'break-word' }}>
              {this.state.error.message || 'Unbekannter Fehler'}
            </Alert>
            <Button variant="contained" onClick={this.handleReload}>
              Seite neu laden
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }
}
